import React, { useContext, useLayoutEffect, useRef } from 'react';

import classNames from 'classnames';

import {
    ModalContext ,
    TModalCloseEvent,
} from './modal-provider';

import { disableScroll, enableScroll } from '../../helpers';
import { Icon } from '../../icon/icon';

export interface IModalInnerProps {
    children: React.ReactNode;
    className?: string;
    isClosable?: boolean;
    isOpen?: boolean;
    onClose?: (event: TModalCloseEvent) => void;
    size?: TModalSize;
}

export type TModalSize = 'small' | 'large';

export const ModalInner = ({
    className,
    children,
    isOpen: propsIsOpen,
    isClosable = true,
    size,
    onClose,
}: IModalInnerProps): JSX.Element => {
    const element: React.MutableRefObject<null | HTMLDivElement> = useRef(null);
    const { close, id, isClosable: contextIsClosable, isOpen: contextIsOpen } = useContext(ModalContext);
    const open: boolean = typeof propsIsOpen !== 'undefined' ? propsIsOpen : contextIsOpen;

    const handleCloseClick: (event: TModalCloseEvent) => void = (event: TModalCloseEvent): void => {
        event.preventDefault();

        if (onClose) {
            onClose(event);
        }

        if (close) {
            close(event);
        }
    };

    const handleBackdropClick: (event: React.MouseEvent<HTMLDivElement>) => void = (event: React.MouseEvent<HTMLDivElement>): void => {
        if (event.target === event.currentTarget) {
            handleCloseClick(event);
        }
    };

    const handleKeyUp: (event: React.KeyboardEvent<HTMLDivElement>) => void = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        // Invoke this.props.onClose when user hits ESC key.
        if (event.code === 'Escape') {
            handleCloseClick(event);
        }
    };

    const shouldBeClosable = (): boolean => !!(isClosable && contextIsClosable);

    const renderModal: () => JSX.Element = (): JSX.Element => {
        const classes: string = classNames(
            'modal__box',
            className,
        );

        return (
            <div className={classes}>
                <div className="modal__box-inner">
                    {shouldBeClosable() &&
                        <button className="modal__close" onClick={handleCloseClick}>
                            <Icon name="close" />
                        </button>
                    }
                    {children}
                </div>
            </div>
        );
    };

    const renderContainer = (): JSX.Element => {
        const classes: string = classNames(
            'modal',
            {
                [`modal--size-${size}`]: size,
                'is-open': open,
                'is-closable': contextIsClosable,
            },
        );

        return (
            <div
                className={classes}
                id={id}
                onKeyUp={handleKeyUp}
                tabIndex={-1}
                ref={element}
            >
                <div className="modal__backdrop" onClick={shouldBeClosable() ? handleBackdropClick : undefined} />
                <div className="modal__inner">
                    {renderModal()}
                </div>
            </div>
        );
    };

    useLayoutEffect(() => {
        if (open) {
            disableScroll();

            if (element.current) {
                element.current.focus();
            }
        } else {
            enableScroll();
        }

        return (): void => {
            if (open) {
                enableScroll();
            }
        };
    }, [open]);

    return renderContainer();
};
